<template>
  <div class="slideshow-container">
    <div class="slideshow-wrapper">
      <!-- Previous Button -->
      <button
        class="nav-btn prev-btn"
        @click.stop="prevSlide"
        v-if="images.length > 1"
        :disabled="currentIndex === 0 && !infinite"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>

      <!-- Image Display -->
      <div class="slide-content">
        <transition name="fade" mode="out-in">
          <img
            :key="currentIndex"
            :src="images[currentIndex]"
            alt="Slideshow Image"
            class="slide-image"
            @click.stop
          />
        </transition>
      </div>

      <!-- Next Button -->
      <button
        class="nav-btn next-btn"
        @click.stop="nextSlide"
        v-if="images.length > 1"
        :disabled="currentIndex === images.length - 1 && !infinite"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>
    </div>

    <!-- Pagination Dots -->
    <div class="pagination-dots" v-if="images.length > 1">
      <span
        v-for="(img, index) in images"
        :key="index"
        class="dot"
        :class="{ active: index === currentIndex }"
        @click.stop="goToSlide(index)"
      ></span>
    </div>

    <!-- Counter/Caption (Optional) -->
    <div class="slide-caption">
      {{ currentIndex + 1 }} / {{ images.length }}
    </div>
  </div>
</template>

<script>
export default {
  name: "TutorialSlideshow",
  props: {
    images: {
      type: Array,
      required: true,
      default: () => [],
    },
    infinite: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentIndex: 0,
    };
  },
  methods: {
    nextSlide() {
      if (this.currentIndex < this.images.length - 1) {
        this.currentIndex++;
      } else if (this.infinite) {
        this.currentIndex = 0;
      }
    },
    prevSlide() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
      } else if (this.infinite) {
        this.currentIndex = this.images.length - 1;
      }
    },
    goToSlide(index) {
      this.currentIndex = index;
    },
  },
  watch: {
    images: {
      handler() {
        this.currentIndex = 0;
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.slideshow-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.slideshow-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  gap: 16px;
}

.slide-content {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 500px; /* Increased from 400px */
  background-color: #f9fafb;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.slide-image {
  max-width: 100%;
  max-height: 700px; /* Increased from 500px */
  height: auto;
  object-fit: contain;
  display: block;
}

.nav-btn {
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.2s;
  color: #4b5563;
  z-index: 2;
}

.nav-btn:hover:not(:disabled) {
  background-color: #f3f4f6;
  transform: scale(1.05);
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-dots {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #e5e7eb;
  cursor: pointer;
  transition: background-color 0.2s;
}

.dot.active {
  background-color: var(
    --primary-color,
    #2563eb
  ); /* Fallback color if var not defined */
  transform: scale(1.1);
}

.slide-caption {
  margin-top: 8px;
  font-size: 14px;
  color: #6b7280;
  font-family: "Century Gothic", sans-serif;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
